import { SavingsBreakdown, SimulationWarning } from '../../../playground/endpoints/_shared';
import { simulateV2Product } from '../../api';
import { ApvSelectOneFormValues } from './schema';

export const SLUG = 'apv-select-one-insurance';

export interface ApvSelectOneCoverage {
  insured_capital: number;
  net_premium: number;
  tax_rate: number;
  gross_premium: number;
}

export interface ApvSelectOnePremiumBreakdown {
  basic: number;
  agreed: number;
  excess: number;
}

export interface ApvSelectOneYearlyRow {
  year: number;
  age: number;
  actuarial_age: number;
  accumulated_prime: number;
  coverage_costs: number;
  policy_expenses: number;
  policy_value: number;
  rentability: number;
  death_compensation: number;
  agreed_deposits_balance: number;
  vp_rescate: number;
  fiscal_bonus_balance: number;
  [key: string]: number | undefined;
}

/**
 * One reported fiscal component of `output/monthly.py::fiscal_breakdown`.
 * `eligible_amount` only ever accompanies the Bonificación Fiscal (Régimen
 * A); `deductible_amount` / `excess_amount` only the Régimen B deduction.
 */
export interface ApvSelectOneFiscalComponent {
  benefit: number;
  running_total: number;
  accumulated_interest: number;
  eligible_amount?: number;
  deductible_amount?: number;
  excess_amount?: number;
}

/**
 * The two regimes are mutually exclusive, so exactly one of
 * `bonificacion_fiscal` / `regime_b_deduction` is present on a given
 * response -- absent, not zeroed. The Agreed Deposit relief is always there:
 * employer money earns it under either regime.
 */
export interface ApvSelectOneFiscalBreakdown {
  agreed_deposit_relief: ApvSelectOneFiscalComponent;
  bonificacion_fiscal?: ApvSelectOneFiscalComponent;
  regime_b_deduction?: ApvSelectOneFiscalComponent;
}

export interface ApvSelectOneMonthlyRow {
  month: number;
  calendar_month: number;
  year: number;
  age: number;
  actuarial_age: number;
  policy_value: number;
  rentability: number;
  net_contribution: number;
  cost: number;
  accumulated_prime: number;
  coverage_costs: number;
  coverage_costs_breakdown?: Record<string, number>;
  policy_expenses: number;
  death_compensation: number;
  agreed_deposits_balance: number;
  vp_rescate: number;
  fiscal_bonus_balance: number;
  fiscal_breakdown?: ApvSelectOneFiscalBreakdown;
}

export interface ApvSelectOneResponse {
  uf_value: number;
  currency: string;
  timeframe_months: number;
  premium: ApvSelectOnePremiumBreakdown;
  coverages: Record<string, ApvSelectOneCoverage | undefined>;
  savings: SavingsBreakdown;
  projection_by_year: ApvSelectOneYearlyRow[];
  projection_by_month?: ApvSelectOneMonthlyRow[];
  warnings: SimulationWarning[];
}

function buildRatePayload(values: ApvSelectOneFormValues) {
  return values.rate_mode === 'fixed'
    ? { rate_mode: 'fixed', projection_rate: values.projection_rate }
    : {
      rate_mode: 'tiered',
      projection_rate_schedule: values.projection_rate_schedule ?? [],
    };
}

export function buildApvSelectOnePayload(values: ApvSelectOneFormValues) {
  const {
    rate_mode: _rateMode,
    projection_rate: _projectionRate,
    projection_rate_schedule: _projectionRateSchedule,
    ...rest
  } = values;
  return {
    ...rest,
    ...buildRatePayload(values),
    compensation_plan: 'B',
    debug: true,
  };
}

export async function simulateApvSelectOne(
  values: ApvSelectOneFormValues,
): Promise<ApvSelectOneResponse> {
  return simulateV2Product<ApvSelectOneResponse>(SLUG, buildApvSelectOnePayload(values));
}
